import React, { useEffect, useMemo, useState } from "react";
import type { HomeBanner } from "../services/api";
import "./Banner.css";

interface BannerProps {
  liveUrl?: string | null;
  userName?: string;
  banners?: HomeBanner[];
}

const Banner: React.FC<BannerProps> = ({ liveUrl, banners = [] }) => {
  const list = useMemo(
    () =>
      banners
        .filter((b) => b.is_active)
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order),
    [banners],
  );
  const [idx, setIdx] = useState(0);
  const active = list[idx] ?? null;

  useEffect(() => {
    setIdx((c) => (list.length ? Math.min(c, list.length - 1) : 0));
  }, [list.length]);

  useEffect(() => {
    if (list.length < 2) return;
    const t = window.setInterval(() => setIdx((c) => (c + 1) % list.length), 6500);
    return () => window.clearInterval(t);
  }, [list.length]);

  const title = active?.title || "Blue Capital";
  const subtitle =
    active?.subtitle ||
    "Cursos, ferramentas e operações ao vivo — tudo em um só lugar.";
  const ctaLabel = active?.cta_label || (liveUrl ? "Entrar ao vivo" : "Explorar catálogo");
  const ctaHref = active?.cta_url || liveUrl || "#/catalogo";
  const external = /^https?:\/\//i.test(ctaHref);
  const image = active?.image_url;

  return (
    <section className="bc-banner" aria-label="Destaque">
      <div
        key={active?.id ?? "default"}
        className="bc-banner-media"
        style={image ? { backgroundImage: `url("${image}")` } : undefined}
      />
      <div className="bc-banner-shade" />
      <div className="bc-banner-in">
        <span className="bc-banner-k">◆ Destaque</span>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <a
          className="bc-banner-cta"
          href={ctaHref}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
        >
          {ctaLabel} <span aria-hidden="true">→</span>
        </a>
      </div>

      {list.length > 1 ? (
        <div className="bc-banner-dots">
          {list.map((b, i) => (
            <button
              key={b.id}
              type="button"
              className={i === idx ? "on" : ""}
              onClick={() => setIdx(i)}
              aria-label={`Ir para o destaque ${i + 1}`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default Banner;
